/**
 * The one hand-off for opening items on the wall (Part 04 decision 11, Part
 * 05 Task 6): fetch each card-only frame's detail in the background,
 * remember the open as Recent, and show the frames in focus over the wall.
 * Focus opens before the detail lands; a failed detail shows the API error
 * toast `ensureFrameDetail` raises.
 */
import { handleApiError } from "@/api/error-middleware";
import { listFrameSummaries } from "@/api/frame-listing";
import { ensureFrameDetail } from "@/lib/frame-detail-requests";
import { loadUnpagedFrames } from "@/lib/frame-ingest";
import {
  openInFocus,
  type OpenFocusOrigin,
} from "@/lib/focus-frame-activation";
import type { FocusCardOrigin } from "@/lib/focus/focus-types";
import { recordRecentFrame } from "@/lib/recent-opened";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";
import { translate } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import { useProjectStore } from "@/stores/project-store";
import { isWallActive } from "@/stores/workspace-view-store";

/** How many ranked matches a title link's search reads. */
const TITLE_SEARCH_LIMIT = 20;

export interface OpenFramesRequest {
  frameIds: readonly string[];
  /** The frame shown active; the first id when omitted. */
  activeId?: string;
  origin: OpenFocusOrigin;
  /** The wall card a card's preview grows from. */
  fromCard?: FocusCardOrigin;
}

/** `frameRef`: the frame's id, or the title a link named it by. */
export function reportMissingFrame(frameRef: string): void {
  useNotificationStore.getState().addSystemToast({
    id: `frame-missing-${frameRef}`,
    level: "error",
    title: translate(layoutMessages, "frameOpenFailedTitle"),
    description: translate(layoutMessages, "frameOpenFailedBody"),
  });
}

/** Open frames in focus over the wall; false when none of them is loaded. */
export function openFrames({
  frameIds,
  activeId,
  origin,
  fromCard,
}: OpenFramesRequest): boolean {
  const { framesById } = useFramesStore.getState();
  // Focus shows only loaded frames; only those are remembered.
  const ids = [...new Set(frameIds)].filter((id) => framesById[id]);
  if (!openInFocus(ids, { activeId, origin, fromCard })) {
    const missing = activeId ?? frameIds[0];
    if (missing) reportMissingFrame(missing);
    return false;
  }
  for (const id of ids) void ensureFrameDetail(id).catch(() => {});
  // The active frame is remembered last, so it leads Recent.
  const active = activeId && ids.includes(activeId) ? activeId : ids[0]!;
  for (const id of ids) if (id !== active) recordRecentFrame(id);
  recordRecentFrame(active);
  return true;
}

/**
 * `openFrames` for frames named by id, which the wall may not have paged
 * in: those are fetched by id first, and the open is dropped when the
 * project changed meanwhile. A frame whose fetch failed is left out (its
 * error toast has shown); one that is not found is reported missing.
 */
export function openFramesById(request: OpenFramesRequest): void {
  const { framesById } = useFramesStore.getState();
  if (request.frameIds.every((id) => framesById[id])) {
    openFrames(request);
    return;
  }
  void loadUnpagedFrames(request.frameIds).then(({ current, failed }) => {
    if (!current) return;
    const frameIds = request.frameIds.filter((id) => !failed.has(id));
    if (frameIds.length > 0) openFrames({ ...request, frameIds });
  });
}

/**
 * Sync contract for callers with a canvas fallback: true when the wall is
 * this device's workspace and an id was given (the open then reports its
 * own failures), false only on the old canvas, where the caller keeps
 * today's path.
 */
export function openFramesNow(
  frameIds: readonly string[],
  origin: OpenFocusOrigin,
): boolean {
  if (!isWallActive() || frameIds.length === 0) return false;
  openFramesById({ frameIds, origin });
  return true;
}

function titleKey(title: string): string {
  return title.trim().toLowerCase();
}

/** The loaded frame with this title, ignoring case and outer spaces. */
export function loadedFrameIdByTitle(title: string): string | null {
  const wanted = titleKey(title);
  if (!wanted) return null;
  const { framesById, frameOrder } = useFramesStore.getState();
  return (
    frameOrder.find((id) => {
      const loaded = framesById[id];
      return !!loaded && titleKey(loaded.title) === wanted;
    }) ?? null
  );
}

async function searchFrameIdByTitle(
  projectId: string,
  title: string,
): Promise<string | null> {
  const page = await listFrameSummaries(projectId, {
    q: title.trim(),
    limit: TITLE_SEARCH_LIMIT,
  });
  const wanted = titleKey(title);
  return page.items.find((item) => titleKey(item.title) === wanted)?.id ?? null;
}

/**
 * `openFramesNow` for a frame named by title: a loaded frame opens at once;
 * else the project's frames are searched for it, since the wall holds only
 * the pages it loaded. False on the old canvas or for a blank title.
 */
export function openFrameTitledNow(
  title: string,
  origin: OpenFocusOrigin,
): boolean {
  if (!isWallActive() || !titleKey(title)) return false;
  const loaded = loadedFrameIdByTitle(title);
  if (loaded) {
    openFrames({ frameIds: [loaded], origin });
    return true;
  }
  const projectId = useProjectStore.getState().currentProject?.id;
  if (!projectId) {
    reportMissingFrame(title);
    return true;
  }
  const isCurrent = () =>
    useProjectStore.getState().currentProject?.id === projectId;
  searchFrameIdByTitle(projectId, title).then(
    (frameId) => {
      if (!isCurrent()) return;
      if (frameId) openFramesById({ frameIds: [frameId], origin });
      else reportMissingFrame(title);
    },
    (error: unknown) => {
      if (isCurrent()) handleApiError(error);
    },
  );
  return true;
}
